import { motion, AnimatePresence } from 'framer-motion';
import { RefreshCw } from 'lucide-react';
import { Card, CardContent } from '@/components/ui/card';
import { Skeleton } from '@/components/ui/skeleton';
import { cn } from '@/lib/utils';
import { StatsPeriod } from '@/hooks/useMultiPeriodFocusStats';

interface FlippableStatsCardProps {
  currentPeriod: StatsPeriod;
  isLoading: boolean;
  icon: React.ReactNode;
  iconBgClass: string;
  renderContent: (period: StatsPeriod) => React.ReactNode;
  onClick: () => void;
}

const contentTransition = { duration: 0.28, ease: [0.25, 0.46, 0.45, 0.94] as const };

const periodLabels: Record<StatsPeriod, string> = {
  today: 'today',
  week: 'this week',
  month: 'this month',
};

export function FlippableStatsCard({
  currentPeriod,
  isLoading,
  icon,
  iconBgClass,
  renderContent,
  onClick,
}: FlippableStatsCardProps) {
  return (
    <div
      className="cursor-pointer"
      onClick={onClick}
    >
      <Card className="border-border/50 task-shadow relative">
        <CardContent className="p-3 sm:p-4">
          {isLoading ? (
            <div className="space-y-2">
              <Skeleton className="h-4 w-4" />
              <Skeleton className="h-6 w-16" />
              <Skeleton className="h-3 w-20" />
            </div>
          ) : (
            <>
              <div className="flex items-start justify-between mb-2">
                <div className={cn("w-7 h-7 sm:w-8 sm:h-8 rounded-lg flex items-center justify-center", iconBgClass)}>
                  {icon}
                </div>
                <RefreshCw className="h-3 w-3 text-muted-foreground/40" />
              </div>
              <div className="min-h-[2.5rem]">
                <AnimatePresence mode="wait" initial={false}>
                  <motion.div
                    key={currentPeriod}
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    exit={{ opacity: 0 }}
                    transition={contentTransition}
                  >
                    {renderContent(currentPeriod)}
                  </motion.div>
                </AnimatePresence>
              </div>
              <p className="text-[10px] sm:text-xs text-muted-foreground mt-1">
                {periodLabels[currentPeriod]}
              </p>
            </>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
